import { Authenticated, Unauthenticated } from '../utils/auth/auth'
import { getCurrentUser } from '../utils/auth/UserProvider'
import { Alert, Box, Button, Typography } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { UUID } from 'crypto'
import { useFundingNeeds } from '@/utils/hooks/fundingNeeds'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'
import { useEffect, useState } from 'react'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import FundingNeedsForm from '@/components/FundingNeeds/FundingNeedsForm'
import FundingNeedsList from '@/components/FundingNeeds/FundingNeedsList'
import FundingIdeaSuggestionsDrawer from '@/components/FundingNeeds/FundingIdeaSuggestionsDrawer'
import LoginSignup from '@/components/LoginSignup'
import UpgradeCTA from '@/components/UpgradeCTA'
import {
  getEffectiveOrgUuid,
  isFreeTier,
  shouldBlockSecondNeed,
} from '@/utils/utils'
import Loading from '@/components/Loading'
import ErrorMessage from '@/components/ErrorMessage'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { usePageTitle } from '@/utils/hooks/usePageTitle'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import type { FundingNeedDraft } from '@/models/FundingNeeds'

export default function FundingRequestsPage() {
  usePageTitle('Funding Requests')
  const { isLoading, user, token, orgs: userOrgs } = getCurrentUser()
  const { isOnboarding } = useOnboardingStatus()
  const { canWrite } = usePausedOrg()
  const focusRef = useFocusManagement()
  const [sparkleOpen, setSparkleOpen] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [prefillDraft, setPrefillDraft] = useState<FundingNeedDraft | null>(
    null,
  )

  const effectiveOrgUuid = getEffectiveOrgUuid(user, userOrgs)
  const {
    data: fundingNeeds,
    isLoading: fundingNeedsLoading,
    error,
  } = useFundingNeeds(token as string, effectiveOrgUuid)

  const {
    orgData,
    isLoading: orgsLoading,
    isError,
  } = useCurrentUserOrgs(token as string, user, userOrgs as UUID[])
  const [organisation, setOrganisation] = useState(orgData?.[0])
  const { isPaused } = usePausedOrg()

  useEffect(() => {
    if (user && orgData && orgData.length > 0) {
      if (!user.selected_org_uuid) {
        setOrganisation(orgData[0])
        return
      } else {
        const selectedOrg = orgData.find(
          (org) => org.uuid === user.selected_org_uuid,
        )
        if (selectedOrg) {
          setOrganisation(selectedOrg || orgData[0])
        }
      }
    }
  }, [orgData, organisation, user])

  if (isLoading || fundingNeedsLoading || orgsLoading) {
    return <Loading />
  }

  if (error || isError) {
    return <ErrorMessage />
  }

  const hasFundingNeeds = Boolean(fundingNeeds && fundingNeeds.length > 0)
  const liveFundingNeedCount =
    fundingNeeds?.filter((need) => !need.is_archived).length ?? 0
  const allFundingNeedsArchived = hasFundingNeeds && liveFundingNeedCount === 0
  const blockSecondNeed = shouldBlockSecondNeed(
    isFreeTier(organisation ?? null),
    liveFundingNeedCount,
  )

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        <>
          <Box
            sx={{
              margin: '0 auto',
              marginTop: 2,
              width: '100%',
              pb: { xs: 14, md: 7 },
              px: { xs: 2, md: 15 },
            }}
          >
            {/* Only show the title when not in onboarding wizard */}
            {!isOnboarding && (
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  mb: 1,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Typography
                  variant="h1"
                  component="h1"
                  ref={focusRef}
                  tabIndex={-1}
                  sx={{
                    mb: 1,
                    textAlign: 'left',
                    fontSize: '34px',
                    '&:focus': {
                      outline: 'none',
                    },
                  }}
                >
                  Funding Requests
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'nowrap',
                  }}
                >
                  <Button
                    variant="outlined"
                    disableRipple
                    disabled={!canWrite}
                    onClick={() => setSparkleOpen(true)}
                    startIcon={<AutoAwesomeIcon />}
                    sx={{
                      color: '#1C3045',
                      borderColor: '#1C3045',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#1C3045',
                        bgcolor: 'rgba(28, 48, 69, 0.04)',
                      },
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                  >
                    Help me create a request
                  </Button>
                  {hasFundingNeeds && (
                    <Button
                      variant="outlined"
                      disableRipple
                      disabled={!canWrite}
                      onClick={() =>
                        blockSecondNeed
                          ? setShowUpgradeDialog(true)
                          : setPrefillDraft({})
                      }
                      sx={{
                        color: '#1C3045',
                        borderColor: '#1C3045',
                        bgcolor: 'white',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#1C3045',
                          bgcolor: 'rgba(28, 48, 69, 0.04)',
                        },
                        '&:focus': {
                          backgroundColor: '#e8e8e8',
                        },
                      }}
                    >
                      Add a Funding Request
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {userOrgs && fundingNeeds && fundingNeeds.length === 0 ? (
              <>
                {!isOnboarding && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      width: { xs: '100%', sm: '80%', md: '60vw' },
                      px: { xs: 3, sm: 6 },
                      mt: 10,
                      height: '20vh',
                      border: 1,
                      borderColor: '#1C3045',
                      borderRadius: 2,
                      mx: 'auto',
                      bgcolor: '#f7f7f7',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 1,
                        fontSize: '20px',
                        textAlign: 'center',
                      }}
                    >
                      {canWrite
                        ? "You don't have any Funding Requests yet!"
                        : "You don't have any Funding Requests."}
                    </Typography>
                    {canWrite && (
                      <Typography
                        variant="caption"
                        sx={{
                          mb: 1,
                          textAlign: 'center',
                          fontSize: '16px',
                        }}
                      >
                        Add a Funding Request below to get started
                      </Typography>
                    )}
                  </Box>
                )}

                {canWrite && (
                  <Box
                    sx={{
                      mt: isOnboarding ? 0 : 4,
                      mx: 'auto',
                      maxWidth: 800,
                    }}
                  >
                    <FundingNeedsForm
                      key={prefillDraft ? 'prefilled' : 'empty'}
                      orgUuid={effectiveOrgUuid}
                      token={token as string}
                      mode="add"
                      prefillDraft={prefillDraft}
                      setPrefillDraft={setPrefillDraft}
                      org_accepts_gambling_proceeds={
                        organisation?.accepts_gambling_proceeds
                      }
                      org_accepts_international={
                        organisation?.accepts_international
                      }
                      org_need_only_context={organisation?.need_only_context}
                      org_region_ids={organisation?.region_ids}
                    />
                  </Box>
                )}
              </>
            ) : (
              <>
                {allFundingNeedsArchived && !isOnboarding && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    All your funding requests are archived. Add a new one above,
                    or turn on &quot;Show Archived&quot; to unarchive an existing
                    request.
                  </Alert>
                )}
                <FundingNeedsList
                  orgUuid={effectiveOrgUuid}
                  token={token as string}
                  org_accepts_gambling_proceeds={
                    organisation?.accepts_gambling_proceeds
                  }
                  org_accepts_international={
                    organisation?.accepts_international
                  }
                  org_need_only_context={organisation?.need_only_context}
                  org_sponsorship_letter={organisation?.sponsorship_letter}
                  org_region_ids={organisation?.region_ids}
                  isAnnualSubscriber={organisation?.is_annual_subscriber === true}
                  prefillDraft={prefillDraft}
                  setPrefillDraft={setPrefillDraft}
                  onPrefillConsumed={() => setPrefillDraft(null)}
                  org_can_set_preferences={organisation?.can_set_preferences}
                  orgWebsite={organisation?.website}
                />
              </>
            )}
          </Box>
        </>
      </Authenticated>
      <FundingIdeaSuggestionsDrawer
        open={sparkleOpen}
        onClose={() => setSparkleOpen(false)}
        onSelectDraft={(draft) => {
          setPrefillDraft({ ...draft })
          setSparkleOpen(false)
        }}
        token={token as string}
        orgUuid={effectiveOrgUuid as string}
        orgAcceptsGambling={organisation?.accepts_gambling_proceeds}
        orgAcceptsInternational={organisation?.accepts_international}
      />
      <UpgradeCTA
        variant="dialog"
        message="Free tier users are limited to 1 Funding Request. Please upgrade to add another."
        ctaText="Upgrade Now"
        isOpen={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
      />
    </>
  )
}
